/* eslint-disable @typescript-eslint/no-unused-vars */
import { brandSeriesModelListUsingPost } from '@/api/queryBrandSeriesModelList';
import { type QueryByPartResp, queryCataloglUsingPost, type Usage } from '@/api/queryCatalog';
import { LayoutForm } from 'xui-pro';
import { useQuery } from '@xpeng/xdragon-ass-components-eu';
import { Button, Form, message, Tag } from 'antd';
import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef
} from 'react';
import { ModalTable } from 'xui-pro';
import { useCreation, useMemoizedFn, useRequest } from 'ahooks';
import EllipsisText from '@/components/EllipsisText';
import styles from './index.module.less';

type ResultColumnData = Usage &
  QueryByPartResp & {
    brandSeriesModelName: string;
    rowSpan: number;
    index: number;
    vin?: string;
  };

export type SearchRef = {
  onPartSearch: (partNo: string) => void;
};

export type SearchProps = {
  /**
   * 当前车型编码，用于初始化表单和判断查询类型
   */
  modelCode?: string;
  /**
   * 当前车架号，用于初始化表单和判断查询类型
   */
  vin?: string;
  /**
   * 搜索命中新的车型/VIN后触发，由父组件负责更新当前展示的车型/VIN并重新查询，
   * 不产生路由跳转。legendCode/partNo用于定位到具体图例/备件
   */
  onSearchLocate: (modelCode?: string, vin?: string, legendCode?: string, partNo?: string) => void;
};

const SearchInline = forwardRef<SearchRef, SearchProps>(
  ({ modelCode, vin, onSearchLocate }, ref) => {
    const [form] = Form.useForm();
    const { displayMode, partNo, lockVin, directPart, legendCode } = useQuery();
    const [type, setType] = useState(modelCode ? 2 : 1);
    const [initialValues] = useState(() => {
      const data = {
        type,
        vin,
        modelCode: modelCode ? [modelCode] : undefined,
        partNo
      };
      form.setFieldsValue(data);
      return data;
    });
    const [values, setValues] = useState<any>(initialValues);
    const defaultModelStr = useRef<string | undefined>('');

    useImperativeHandle(ref, () => {
      return {
        onPartSearch: partNoV => {
          const _values: any = { partNo: partNoV, vin: '', modelCode: '', directPart: 0 };
          if (displayMode === 'NONE') {
            if (lockVin === '1' && vin) _values.vin = vin;
            _values.directPart = Number(directPart);
          }
          form.setFieldsValue({ ..._values });
          setValues(form.getFieldsValue());
          form.submit();
        }
      };
    });

    const { data: brandOpt } = useRequest(async () => {
      const res = await brandSeriesModelListUsingPost();
      if (res.code !== 200) {
        message.error(res.msg);
        return [];
      }
      const brandList = res.data.brandList ?? [];
      const treeData = brandList.map(brand => {
        const seriesList =
          brand.seriesList?.map(series => {
            const modelList = series.modelList?.map(model => {
              const str = `${brand.brandName}/${series.seriesName}/${model.modelName}`;
              if (modelCode === model.modelCode) {
                defaultModelStr.current = str;
              }
              return {
                value: model.modelCode,
                label: model.modelName,
                showLabel: str
              };
            });
            return {
              value: series.seriesCode,
              label: series.seriesName,
              selectable: false,
              children: modelList
            };
          }) ?? [];
        return {
          value: brand.brandCode,
          label: brand.brandName,
          selectable: false,
          children: seriesList
        };
      });
      return treeData;
    });

    const formItems: any = useMemo(() => {
      const items: any[] = [
        {
          name: 'type',
          label: '',
          type: 'radio',
          options: [
            {
              label: 'VIN码查找',
              value: 1
            },
            {
              label: '车型查找',
              value: 2,
              disabled: lockVin === '1'
            }
          ],
          props: {
            optionType: 'button'
          },
          style: { width: '200px' }
        },
        {
          name: 'partNo',
          label: '备件',
          type: 'input',
          props: {
            placeholder: '请输入备件编码或备件名称查询'
          },
          style: { width: '300px' }
        }
      ];
      if (type === 1) {
        items.splice(1, 0, {
          name: 'vin',
          label: 'VIN码',
          type: 'input',
          props: {
            placeholder: '请输入VIN码'
          },
          disabled: lockVin === '1',
          style: { width: '300px' }
        });
      } else if (type === 2) {
        items.splice(1, 0, {
          name: 'modelCode',
          label: '品牌车系车型',
          type: 'cascader',
          style: { width: '350px' },
          props: {
            showSearch: {
              filter: (inputValue, path) => {
                return path.some(option => option.showLabel?.includes(inputValue));
              }
            },
            popupClassName: styles['search-car-cascader'],
            displayRender: (_, selectedOptions) => {
              if (!selectedOptions.filter(v => !!v).length) return defaultModelStr.current;
              return selectedOptions[selectedOptions.length - 1].showLabel;
            },
            allowClear: true,
            multiple: false,
            expandTrigger: 'hover',
            showCheckedStrategy: 'SHOW_CHILD'
          },
          options: brandOpt ?? []
        });
      }
      return items;
    }, [type, brandOpt, lockVin]);

    const [searchResult, setSearchResult] = useState<ResultColumnData[] | undefined>();

    const toPartDetail = useCallback(
      (record: ResultColumnData) => {
        // 备件详情：切换到命中记录对应的车型/VIN，并定位到对应图例/备件，不产生路由跳转
        onSearchLocate(record.modelCode, record.vin, record.legendCode, record.partNo);
      },
      [onSearchLocate]
    );

    const { run: onFinish, loading: submitLoading } = useRequest(
      async formValues => {
        if (formValues.modelCode && formValues.modelCode.length) {
          formValues.modelCode = formValues.modelCode[formValues.modelCode.length - 1];
        }
        formValues.directPart = Number(directPart);
        if (formValues.partNo) {
          const res = await queryCataloglUsingPost(formValues);
          if (res.code !== 200) {
            message.error(res.msg);
            return;
          }
          const data: ResultColumnData[] = [];
          let rowIndex = 0;
          (res.data.partList ?? []).forEach(part => {
            if ((part.usageList?.length ?? 0) > 0) rowIndex++;
            (part.usageList ?? []).forEach((usage, index) => {
              const brandSeriesModelName = `${usage.brandName}/${usage.seriesName}/${usage.modelName}`;
              data.push({
                ...part,
                ...usage,
                // 仅VIN码查找场景才携带vin，避免车型查找命中结果时误带上接口返回的vehicleInfo.vin，
                // 导致后续按vin+modelCode一起查询，命中与当前车型不一致的目录数据
                vin: formValues.type === 1 ? res.data.vehicleInfo?.vin : undefined,
                brandSeriesModelName,
                rowSpan: index === 0 ? part.usageList?.length ?? 0 : 0,
                index: rowIndex
              });
            });
          });
          if (data.length) {
            if (data.length === 1) {
              toPartDetail(data[0]);
            } else {
              setSearchResult(data);
            }
          } else {
            message.error('未查询到相关备件关联用法');
          }
        } else {
          if (formValues.type === 1) {
            if (formValues.vin) {
              const res = await queryCataloglUsingPost(formValues);
              if (res.code !== 200) {
                message.error(res.msg);
                return;
              }
              onSearchLocate(res.data.vehicleInfo?.modelCode, formValues.vin);
            }
          } else if (formValues.type === 2) {
            if (formValues.modelCode) {
              onSearchLocate(formValues.modelCode, undefined);
            }
          }
        }
      },
      { manual: true }
    );

    const autoFetch = useMemoizedFn(() => {
      onFinish({ modelCode: [modelCode], partNo });
    });

    useCreation(() => {
      if (!legendCode) autoFetch();
    }, []);
    const resultColumns: any = useMemo(() => {
      return [
        {
          title: '序号',
          width: 60,
          dataIndex: 'index',
          onCell: (record: ResultColumnData) => {
            return {
              rowSpan: record.rowSpan
            };
          }
        },
        {
          title: '备件编码',
          dataIndex: 'partNo',
          width: 180,
          render: (_, record: ResultColumnData) => {
            return (
              <div>
                <EllipsisText text={record.partNo ?? ''} width={'auto'} />
                <EllipsisText
                  text={record.partName ?? ''}
                  width={'auto'}
                  style={{ fontSize: '12px' }}
                />
                <div style={{ display: 'flex' }}>
                  {record.saleState === 0 && <Tag color='red'>停售</Tag>}
                </div>
              </div>
            );
          },
          onCell: (record: ResultColumnData) => {
            return {
              rowSpan: record.rowSpan
            };
          }
        },
        {
          title: '品牌/车系/车型',
          dataIndex: 'brandSeriesModelName'
        },
        {
          title: '主组',
          dataIndex: 'groupName'
        },
        {
          title: '图例名称',
          dataIndex: 'legendName'
        },
        {
          title: '操作',
          render: (_, record) => {
            return <a onClick={() => toPartDetail(record)}>查看</a>;
          }
        }
      ];
    }, [toPartDetail]);

    const onValuesChange = useCallback((_, allFields) => {
      setValues(allFields);
    }, []);

    const disable = useMemo(() => {
      const hasPartNo = !!form.getFieldValue('partNo');
      if (hasPartNo) return false;
      return type === 1 ? !form.getFieldValue('vin') : !form.getFieldValue('modelCode');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values, type]);

    return (
      <>
        <LayoutForm
          formItems={formItems}
          form={form}
          onFieldsChange={value => {
            if (value && value.length && value[0].name && value[0].name[0] === 'type') {
              setType(value[0].value);
            }
          }}
          mode={'form'}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          style={{
            padding: '12px',
            paddingBottom: '0',
            background: '#ffffff',
            borderRadius: '6px',
            width: '100%',
            marginBottom: '12px'
          }}
        >
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button type='primary' htmlType='submit' loading={submitLoading} disabled={disable}>
              查询
            </Button>
          </div>
        </LayoutForm>
        {searchResult && (
          <ModalTable
            columns={resultColumns}
            visible={true}
            modalSize='xLarge'
            footer={null}
            onCancel={() => setSearchResult(undefined)}
            dataSource={searchResult}
            pagination={false}
            className={styles['search-result']}
            toolbarOptions={{
              density: false,
              reload: false,
              setting: false
            }}
            height={`calc(100vh - 350px)`}
          />
        )}
      </>
    );
  }
);
const Search = React.memo(SearchInline);
export default Search;
